<script setup>
import Select from 'dashboard/components-next/select/Select.vue';
import { useEventListener } from '@vueuse/core';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import BankLogo from '../settings/integrations/BankLogo.vue';
/* eslint-disable no-use-before-define */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import TalkviaFactaOfflineBatchesAPI from 'dashboard/api/talkviaFactaOfflineBatches';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import DatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { format } from 'date-fns';
import { batchProgress } from './batchProgress';
import {
  scheduleMinimum,
  scheduleToUtc,
  SCHEDULE_TIME_ZONE,
} from './scheduleTime';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';

const store = useStore();

// O token identifica o lote nos logs e na auditoria do MCP; o suporte usa esse código.
const copySupportToken = async token => {
  try {
    await copyTextToClipboard(token);
    useAlert(
      `Token ${token} copiado. Informe-o ao suporte para localizar o lote.`
    );
  } catch {
    useAlert('Não foi possível copiar o token. Selecione e copie manualmente.');
  }
};

const batches = ref([]);
const selectedBatch = ref(null);
const integration = ref({ configured: false, active: false, providers: [] });
const isLoading = ref(false);
const isLoadingMore = ref(false);
const isLoadingResults = ref(false);
const resultsPage = ref(1);
const resultsPerPage = ref(50);
let resultsRequest = 0;
let historyRequest = 0;
let refreshInFlight = false;
const pagination = ref({ page: 1, hasMore: false, nextPage: null });
const isCreating = ref(false);
const isCreateModalOpen = ref(false);
const showAllCpfs = ref(false);
const logResult = ref(null);
const labelSearch = ref('');
const activeBatchActionsId = ref(null);
const fileInput = ref(null);
const dateRange = ref();
const rangeType = ref();
const showDateRange = ref(false);
const progressSample = ref(null);
let pollTimer = null;
let audienceRequest = 0;
const labelAudienceCount = ref(0);
const isLoadingAudience = ref(false);
const audienceError = ref(false);
const labelAudienceReady = computed(
  () =>
    form.audience_mode !== 'labels' ||
    (!isLoadingAudience.value &&
      !audienceError.value &&
      labelAudienceCount.value > 0)
);

const audienceModeOptions = [
  { value: 'paste', label: 'Colar', icon: 'i-hugeicons-clipboard' },
  { value: 'labels', label: 'Etiquetas', icon: 'i-hugeicons-tags' },
  { value: 'file', label: 'Arquivo', icon: 'i-hugeicons-file-upload' },
];

const filters = reactive({
  created_from: '',
  created_to: '',
});

const form = reactive({
  title: '',
  query_mode: 'offline',
  product_type: 'CLT',
  provider_codes: [],
  audience_mode: 'paste',
  cpfs: '',
  label_titles: '',
  file: null,
  start_mode: 'now',
  scheduled_at: '',
  enrich_customer_data: true,
});

const startModeOptions = [
  { value: 'now', label: 'Iniciar agora', icon: 'i-hugeicons-play' },
  { value: 'scheduled', label: 'Agendar', icon: 'i-hugeicons-calendar-03' },
];
const isScheduledStart = computed(() => form.start_mode === 'scheduled');
const submitLabel = computed(() =>
  isScheduledStart.value ? 'Criar e agendar lote' : 'Criar e iniciar lote'
);

const batchModes = [
  {
    value: 'offline',
    label: 'Lote Offline',
    icon: 'i-hugeicons-database-02',
    description: 'Vínculos e margem da base disponível, sem simular ofertas.',
  },
  {
    value: 'online',
    label: 'Lote Online',
    icon: 'i-hugeicons-calculator-01',
    description:
      'Simulação completa. Uma oferta por CPF, com o maior valor liberado entre os bancos.',
  },
];
const isOnlineBatch = computed(
  () => selectedBatch.value?.query_mode === 'online'
);
const resultFilter = ref('all');
const resultsPagination = computed(
  () =>
    selectedBatch.value?.results_pagination || {
      page: 1,
      per_page: resultsPerPage.value,
      total: selectedBatch.value?.results?.length || 0,
      total_pages: 1,
      has_more: false,
    }
);
const resultsRange = computed(() => {
  const { page, per_page: perPage, total } = resultsPagination.value;
  return {
    start: total ? (page - 1) * perPage + 1 : 0,
    end: Math.min(page * perPage, total),
    total,
  };
});
const money = value =>
  value === null || value === undefined || value === ''
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value));
const batchModeLabel = batch =>
  batch?.query_mode === 'online' ? 'Lote Online' : 'Lote Offline';
const hasRetryableResults = computed(
  () =>
    isOnlineBatch.value &&
    ['completed', 'partial_success', 'failed', 'timeout'].includes(
      selectedBatch.value?.status
    ) &&
    [
      'authorization_required',
      'missing_fields',
      'error',
      'timeout',
      'awaiting_confirmation',
    ].some(
      status =>
        Number(selectedBatch.value?.result_status_counts?.[status] || 0) > 0
    )
);

const offlineProviders = computed(() => integration.value.providers || []);
// O Offline só executa os bancos com rotina na base (Facta e Presença); o Online
// consulta todos os bancos ativos e para, pendente, onde o banco exige o cliente.
const batchProviders = computed(() =>
  offlineProviders.value.filter(provider => provider.batch_supported !== false)
);
const canUseProvider = provider => {
  if (!provider.configured) return false;
  if (form.query_mode === 'online') return provider.online_supported !== false;
  return provider.batch_supported !== false;
};
const providerUnavailableReason = provider => {
  if (!provider.configured) return 'Ative a integração para usar no lote.';
  if (form.query_mode === 'online') return provider.online_notice || '';
  return provider.unavailable_reason || '';
};
const bankOptions = computed(() =>
  offlineProviders.value
    .map(provider => ({
      value: provider.code,
      label: provider.label,
      disabled: !canUseProvider(provider),
      reason: providerUnavailableReason(provider),
    }))
    .sort((a, b) => Number(a.disabled) - Number(b.disabled))
);
const confirmeOnline = computed(() => integration.value.confirme_online || {});
watch(
  () => form.query_mode,
  () => {
    form.provider_codes = form.provider_codes.filter(code =>
      offlineProviders.value.some(
        provider => provider.code === code && canUseProvider(provider)
      )
    );
    if (!form.provider_codes.length) selectConfiguredProviders();
  }
);
const availableBankCount = computed(
  () => bankOptions.value.filter(option => !option.disabled).length
);
const selectedProviders = computed(() =>
  offlineProviders.value.filter(provider =>
    form.provider_codes.includes(provider.code)
  )
);
const canCreateBatch = computed(
  () =>
    selectedProviders.value.length > 0 &&
    selectedProviders.value.every(canUseProvider) &&
    (!isScheduledStart.value || Boolean(form.scheduled_at))
);

const selectConfiguredProviders = () => {
  form.provider_codes = offlineProviders.value
    .filter(canUseProvider)
    .map(provider => provider.code);
};

const currentResults = computed(() => selectedBatch.value?.results || []);

const accountLabels = computed(() => store.getters['labels/getLabels'] || []);

const selectedLabelTitles = computed(() => splitLabels(form.label_titles));

const filteredLabels = computed(() => {
  const query = labelSearch.value.trim().toLowerCase();
  return accountLabels.value
    .filter(label => !query || label.title.toLowerCase().includes(query))
    .slice(0, 30);
});

const hasSelectedLabel = title => selectedLabelTitles.value.includes(title);

const toggleLabel = title => {
  const next = new Set(selectedLabelTitles.value);
  if (next.has(title)) next.delete(title);
  else next.add(title);
  form.label_titles = Array.from(next).join(',');
};

const batchActionsTrigger = ref(null);
const batchActionsMenu = ref(null);
const batchActionsOpen = computed(() => activeBatchActionsId.value !== null);
const {
  fixedPosition: batchActionsPosition,
  updatePosition: updateBatchActionsPosition,
} = useDropdownPosition(
  batchActionsTrigger,
  batchActionsMenu,
  batchActionsOpen
);
const toggleBatchActions = async (batchId, event) => {
  if (activeBatchActionsId.value === batchId) {
    closeBatchActions();
    return;
  }
  batchActionsTrigger.value = event.currentTarget;
  activeBatchActionsId.value = batchId;
  await nextTick();
  updateBatchActionsPosition();
  batchActionsMenu.value?.querySelector('button:not(:disabled)')?.focus();
};
const closeBatchActions = () => {
  activeBatchActionsId.value = null;
};
const closeBatchActionsWithFocus = () => {
  closeBatchActions();
  batchActionsTrigger.value?.focus();
};
useEventListener(document, 'click', event => {
  if (
    !batchActionsMenu.value?.contains(event.target) &&
    !batchActionsTrigger.value?.contains(event.target)
  )
    closeBatchActions();
});
useEventListener(
  window,
  'scroll',
  event => {
    if (!batchActionsMenu.value?.contains(event.target)) closeBatchActions();
  },
  { capture: true }
);
useEventListener(window, 'resize', closeBatchActions);

const canPauseBatch = batch =>
  ['awaiting', 'processing'].includes(batch?.status);
const canResumeBatch = batch => batch?.status === 'paused';
const canCancelBatch = batch =>
  batch &&
  !['completed', 'partial_success', 'failed', 'timeout', 'cancelled'].includes(
    batch.status
  );

const currentDateTime = ref(scheduleMinimum());

const splitLabels = value =>
  String(value || '')
    .split(/[,;\n]/)
    .map(item => item.trim())
    .filter(Boolean);

const extractCpfCandidates = value => {
  const content = String(value || '');
  const candidates = [
    ...content.split(/[,;\r\n]+/),
    ...(content.match(/\d{3}\.?\d{3}\.?\d{3}-?\d{2}/g) || []),
  ];

  return Array.from(
    new Set(
      candidates
        .map(candidate => candidate.replace(/\D/g, ''))
        .filter(candidate => candidate.length === 11)
    )
  );
};

const normalizeCpfList = value => extractCpfCandidates(value).join(', ');

const normalizeCpfs = () => {
  form.cpfs = normalizeCpfList(form.cpfs);
};

const audienceCount = computed(() => {
  if (form.audience_mode === 'labels') return labelAudienceCount.value;
  if (form.audience_mode === 'file') return form.file ? 1 : 0;
  return extractCpfCandidates(form.cpfs).length;
});

watch(
  [() => form.audience_mode, () => form.label_titles, isCreateModalOpen],
  async () => {
    audienceRequest += 1;
    const request = audienceRequest;
    labelAudienceCount.value = 0;
    audienceError.value = false;
    isLoadingAudience.value = false;
    if (
      !isCreateModalOpen.value ||
      form.audience_mode !== 'labels' ||
      !selectedLabelTitles.value.length
    )
      return;
    isLoadingAudience.value = true;
    try {
      const { data } = await TalkviaFactaOfflineBatchesAPI.audience(
        selectedLabelTitles.value
      );
      if (request === audienceRequest) labelAudienceCount.value = data.count;
    } catch {
      if (request === audienceRequest) audienceError.value = true;
    } finally {
      if (request === audienceRequest) isLoadingAudience.value = false;
    }
  },
  { flush: 'sync' }
);

const mergeBatch = batch => {
  const index = batches.value.findIndex(item => item.id === batch.id);
  if (index >= 0)
    batches.value.splice(index, 1, { ...batches.value[index], ...batch });
  else batches.value.unshift(batch);
};

const selectFirstBatch = async () => {
  if (!selectedBatch.value && batches.value.length)
    await selectBatch(batches.value[0]);
};

watch(selectedBatch, (batch, previous) => {
  if (
    !batch ||
    batch.id !== previous?.id ||
    batch.status !== previous?.status
  ) {
    progressSample.value =
      batch?.status === 'processing'
        ? {
            id: batch.id,
            processed: Number(batch.processed || 0),
            at: Date.now(),
          }
        : null;
  }
});
const progress = computed(() =>
  batchProgress(selectedBatch.value, progressSample.value)
);

const loadBatches = async ({
  silent = false,
  page = 1,
  append = false,
} = {}) => {
  historyRequest += 1;
  const request = historyRequest;
  if (!silent && !append) isLoading.value = true;
  if (append) isLoadingMore.value = true;
  try {
    const { data } = await TalkviaFactaOfflineBatchesAPI.get({
      page,
      per_page: 25,
      created_from: filters.created_from,
      created_to: filters.created_to,
    });
    if (request !== historyRequest) return;
    const incoming = data.batches || [];
    const preservePages = silent && !append && pagination.value.page > 1;
    if (append || preservePages) {
      const merged = new Map(batches.value.map(batch => [batch.id, batch]));
      incoming.forEach(batch =>
        merged.set(batch.id, { ...merged.get(batch.id), ...batch })
      );
      batches.value = preservePages
        ? [
            ...incoming.map(batch => merged.get(batch.id)),
            ...[...merged.values()].filter(
              batch => !incoming.some(item => item.id === batch.id)
            ),
          ]
        : [...merged.values()];
    } else {
      batches.value = incoming;
    }
    if (!preservePages)
      pagination.value = {
        page: data.pagination?.page || page,
        hasMore: Boolean(data.pagination?.has_more),
        nextPage: data.pagination?.next_page || null,
      };
    integration.value = data.integration || integration.value;
    if (!form.provider_codes.length) selectConfiguredProviders();
    if (!silent && !append) await selectFirstBatch();
  } catch {
    if (!silent && request === historyRequest)
      useAlert('Não foi possível carregar os lotes CLT.');
  } finally {
    if (request === historyRequest) {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  }
};

const selectBatch = async (batch, { silent = false } = {}) => {
  if (selectedBatch.value?.id !== batch.id) {
    resultFilter.value = 'all';
    resultsPage.value = 1;
    selectedBatch.value = { ...batch, results: [] };
    logResult.value = null;
    showAllCpfs.value = false;
  }
  resultsRequest += 1;
  const request = resultsRequest;
  if (!silent) isLoadingResults.value = true;
  try {
    const { data } = await TalkviaFactaOfflineBatchesAPI.show(batch.id, {
      results_page: resultsPage.value,
      results_per_page: resultsPerPage.value,
      result_status: resultFilter.value,
    });
    if (request !== resultsRequest) return;
    selectedBatch.value = data;
    resultsPage.value = data.results_pagination?.page || 1;
    mergeBatch(data);
  } catch {
    if (request === resultsRequest) {
      resultsPage.value = resultsPagination.value.page;
      resultFilter.value = resultsPagination.value.result_status || 'all';
      if (!silent) useAlert('Não foi possível carregar o lote.');
    }
  } finally {
    if (request === resultsRequest) isLoadingResults.value = false;
  }
};

const changeResultFilter = async filter => {
  if (!selectedBatch.value || resultFilter.value === filter) return;
  resultFilter.value = filter;
  resultsPage.value = 1;
  await selectBatch(selectedBatch.value);
};

const changeResultsPage = async page => {
  if (
    isLoadingResults.value ||
    page < 1 ||
    page > resultsPagination.value.total_pages
  )
    return;
  resultsPage.value = page;
  await selectBatch(selectedBatch.value);
};

const changeResultsPerPage = async value => {
  resultsPerPage.value = Number(value);
  resultsPage.value = 1;
  await selectBatch(selectedBatch.value);
};

const refreshBatches = async () => {
  if (
    refreshInFlight ||
    isLoading.value ||
    isLoadingMore.value ||
    isLoadingResults.value
  )
    return;
  refreshInFlight = true;
  try {
    await loadBatches({ silent: true, page: 1 });
    if (selectedBatch.value && !isLoadingResults.value)
      await selectBatch(selectedBatch.value, { silent: true });
  } finally {
    refreshInFlight = false;
  }
};

const loadMoreBatches = async () => {
  if (!pagination.value.hasMore || isLoadingMore.value) return;
  await loadBatches({
    silent: true,
    append: true,
    page: pagination.value.nextPage,
  });
};

const onBatchesScroll = event => {
  const target = event.target;
  const remaining =
    target.scrollHeight - target.scrollTop - target.clientHeight;
  if (remaining < 80) loadMoreBatches();
};

const applyDateFilters = async () => {
  resultsRequest += 1;
  selectedBatch.value = null;
  isLoadingResults.value = false;
  await loadBatches({ page: 1 });
};

const changeDateRange = async ([start, end, type]) => {
  dateRange.value = [start, end];
  rangeType.value = type;
  filters.created_from = format(start, 'yyyy-MM-dd');
  filters.created_to = format(end, 'yyyy-MM-dd');
  await applyDateFilters();
};
const clearDateRange = async () => {
  filters.created_from = '';
  filters.created_to = '';
  dateRange.value = undefined;
  rangeType.value = undefined;
  showDateRange.value = false;
  await applyDateFilters();
};

const createBatch = async () => {
  if (!labelAudienceReady.value || isCreating.value || !canCreateBatch.value)
    return;
  const pastedCpfs = normalizeCpfList(form.cpfs);
  const hasAudience =
    (form.audience_mode === 'paste' && pastedCpfs) ||
    (form.audience_mode === 'file' && form.file) ||
    (form.audience_mode === 'labels' && form.label_titles.trim());

  if (!hasAudience) {
    useAlert('Cole CPFs, envie um arquivo ou informe etiquetas.');
    return;
  }

  if (form.audience_mode === 'paste') form.cpfs = pastedCpfs;

  const data = new FormData();
  data.append('title', form.title);
  data.append('query_mode', form.query_mode);
  data.append('product_type', form.product_type);
  form.provider_codes.forEach(provider =>
    data.append('provider_codes[]', provider)
  );
  data.append('audience_mode', form.audience_mode);
  data.append('cpfs', form.audience_mode === 'paste' ? pastedCpfs : '');
  data.append(
    'label_titles',
    form.audience_mode === 'labels' ? form.label_titles : ''
  );
  data.append(
    'scheduled_at',
    isScheduledStart.value ? scheduleToUtc(form.scheduled_at) : ''
  );
  if (form.query_mode === 'online')
    data.append('enrich_customer_data', String(form.enrich_customer_data));
  if (form.audience_mode === 'file' && form.file)
    data.append('file', form.file);

  isCreating.value = true;
  try {
    const response = await TalkviaFactaOfflineBatchesAPI.create(data);
    mergeBatch(response.data);
    resultsRequest += 1;
    resultFilter.value = 'all';
    resultsPage.value = 1;
    resultsPerPage.value = response.data.results_pagination?.per_page || 50;
    selectedBatch.value = response.data;
    isLoadingResults.value = false;
    resetForm();
    closeCreateModal();
    useAlert('Lote criado. Os bancos serão consultados em segundo plano.');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não foi possível criar o lote.');
  } finally {
    isCreating.value = false;
  }
};

const onFileChange = event => {
  form.file = event.target.files?.[0] || null;
};

const resetForm = () => {
  form.title = '';
  form.product_type = 'CLT';
  selectConfiguredProviders();
  form.audience_mode = 'paste';
  form.cpfs = '';
  form.label_titles = '';
  form.file = null;
  form.start_mode = 'now';
  form.scheduled_at = '';
  if (fileInput.value) fileInput.value.value = '';
};

const openCreateModal = (mode = 'offline') => {
  form.query_mode = typeof mode === 'string' ? mode : 'offline';
  isCreateModalOpen.value = true;
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
};

const toggleAllCpfs = () => {
  showAllCpfs.value = !showAllCpfs.value;
};

const displayCpf = result =>
  showAllCpfs.value ? result.cpf_full || result.cpf : result.cpf;

const providerLabel = code =>
  offlineProviders.value.find(provider => provider.code === code)?.label ||
  code;

const openLog = result => {
  logResult.value = result;
};

const closeLog = () => {
  logResult.value = null;
};

const exportDialog = ref(null);
const exportBatch = ref(null);
const exportStatus = ref('all');
const isDownloading = ref(false);

const openCsvDownload = batch => {
  closeBatchActions();
  exportBatch.value = batch;
  exportStatus.value = 'all';
  exportDialog.value?.open();
};

const downloadCsv = async () => {
  if (isDownloading.value || !exportBatch.value) return;
  const batch = exportBatch.value;
  isDownloading.value = true;
  try {
    const response = await TalkviaFactaOfflineBatchesAPI.download(
      batch.id,
      exportStatus.value
    );
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `consulta-${batch.query_mode || 'offline'}-clt-${batch.id}-${exportStatus.value === 'success' ? 'sucesso' : 'todos'}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    exportDialog.value?.close();
  } catch {
    useAlert('Não foi possível baixar o CSV.');
  } finally {
    isDownloading.value = false;
  }
};

const deleteBatch = async batch => {
  closeBatchActions();
  try {
    await TalkviaFactaOfflineBatchesAPI.destroy(batch.id);
    batches.value = batches.value.filter(item => item.id !== batch.id);
    if (selectedBatch.value?.id === batch.id) {
      resultsRequest += 1;
      selectedBatch.value = null;
      isLoadingResults.value = false;
      await selectFirstBatch();
    }
    useAlert('Lote excluído.');
  } catch {
    useAlert('Não foi possível excluir o lote.');
  }
};

const updateBatchStatus = async (batch, action) => {
  closeBatchActions();
  try {
    const { data } = await TalkviaFactaOfflineBatchesAPI[action](batch.id);
    mergeBatch(data);
    if (selectedBatch.value?.id === batch.id)
      await selectBatch(selectedBatch.value);
    useAlert('Lote atualizado.');
  } catch {
    useAlert('Não foi possível atualizar o lote.');
  }
};

const batchHasErrors = batch =>
  Number(batch?.error_count || 0) + Number(batch?.timeout_count || 0) > 0;

const batchVisualStatus = batch => {
  if (batch?.status === 'partial_success' && !batchHasErrors(batch))
    return 'completed';
  return batch?.status;
};

const recentBatchCardClass = batch => [
  `batch-${batchVisualStatus(batch)}`,
  selectedBatch.value?.id === batch.id ? '!border-n-slate-7 !bg-n-alpha-2' : '',
];

const statusLabel = status =>
  ({
    completed: 'Finalizado',
    processing: 'Em processamento',
    paused: 'Pausado',
    cancelled: 'Cancelado',
    partial_success: 'Concluído com erro',
    failed: 'Concluído com erro',
    timeout: 'Concluído com erro',
    awaiting: 'Não iniciado',
    success: 'Concluído',
    authorization_required: 'Autorização pendente',
    missing_fields: 'Dados pendentes',
    awaiting_confirmation: 'Aguardando confirmação',
    no_offer: 'Sem oferta',
    no_margin: 'Sem margem',
    error: 'Falha na consulta',
  })[status] || status;

const resultClass = status =>
  ({
    completed: 'text-n-teal-11',
    success: 'text-n-teal-11',
    authorization_required: 'text-n-amber-11',
    missing_fields: 'text-n-amber-11',
    no_margin: 'text-n-slate-11',
    partial_success: 'text-n-amber-11',
    failed: 'text-n-ruby-11',
    error: 'text-n-ruby-11',
    timeout: 'text-n-ruby-11',
    processing: 'text-n-blue-11',
    paused: 'text-n-blue-11',
    awaiting: 'text-n-slate-11',
    cancelled: 'text-n-ruby-11',
  })[status] || 'text-n-slate-11';

// Resultado do lote: data com hora, minuto e segundo, para acompanhar cada consulta.
const formatDateTimeSeconds = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: SCHEDULE_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value));
};

// Reprocessamento individual (Lote Online): resultados encerrados sem oferta confirmada.
const SINGLE_RETRY_STATUSES = [
  'error',
  'timeout',
  'authorization_required',
  'missing_fields',
  'awaiting_confirmation',
  'no_offer',
  'no_margin',
];
const retryDialog = ref(null);
const retryTarget = ref(null);
const retryingResultId = ref(null);
const canRetryResult = result =>
  isOnlineBatch.value &&
  !result.best_offer &&
  SINGLE_RETRY_STATUSES.includes(result.status);
const openRetryResult = result => {
  retryTarget.value = result;
  retryDialog.value?.open();
};
const retryResult = async () => {
  const result = retryTarget.value;
  if (!result || !selectedBatch.value || retryingResultId.value) return;
  retryingResultId.value = result.id;
  try {
    await TalkviaFactaOfflineBatchesAPI.retryResult(
      selectedBatch.value.id,
      result.id
    );
    retryDialog.value?.close();
    useAlert('CPF enviado para reprocessamento.');
    await selectBatch(selectedBatch.value, { silent: true });
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível reprocessar este CPF. Tente novamente.'
    );
  } finally {
    retryingResultId.value = null;
  }
};

const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: SCHEDULE_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const startPolling = () => {
  pollTimer = window.setInterval(() => {
    currentDateTime.value = scheduleMinimum();
    refreshBatches();
  }, 8000);
};

onMounted(async () => {
  await store.dispatch('labels/get');
  await loadBatches();
  startPolling();
});

onBeforeUnmount(() => {
  audienceRequest += 1;
  resultsRequest += 1;
  historyRequest += 1;
  if (pollTimer) window.clearInterval(pollTimer);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline, vue/max-attributes-per-line -->
  <main
    class="flex h-full min-h-0 flex-col overflow-auto p-4 md:p-6 text-n-slate-12"
    @click="closeBatchActions"
  >
    <header
      class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
    >
      <div class="max-w-3xl">
        <div
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-brand"
        >
          Consulta Lote CLT
        </div>
        <h1 class="m-0 text-2xl font-semibold">Do público à melhor oferta</h1>
        <p class="mb-0 mt-2 text-base leading-7 text-n-slate-11">
          Escolha entre consultar a base Offline ou simular ofertas Online.
          Organize os CPFs, acompanhe cada banco e exporte os resultados.
        </p>
      </div>
      <div
        class="grid min-w-72 gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3 text-sm"
      >
        <div
          v-for="provider in batchProviders"
          :key="provider.code"
          class="flex items-center justify-between gap-3"
        >
          <span class="flex items-center gap-2 font-medium text-n-slate-12"
            ><BankLogo :provider="provider.code" />{{ provider.label }}</span
          >
          <span
            class="rounded-full px-2 py-1 text-xs font-semibold"
            :class="
              provider.configured
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{ provider.configured ? 'Ativa' : 'Não configurada' }}
          </span>
        </div>
      </div>
    </header>

    <div class="mb-5 grid gap-3 md:grid-cols-2">
      <button
        v-for="mode in batchModes"
        :key="mode.value"
        type="button"
        class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 text-left transition hover:border-n-brand focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="openCreateModal(mode.value)"
      >
        <span :class="mode.icon" class="mt-1 size-6 shrink-0 text-n-brand" />
        <span class="min-w-0 flex-1">
          <strong class="block text-base text-n-slate-12">{{
            mode.label
          }}</strong>
          <span class="mt-1 block text-sm text-n-slate-11">{{
            mode.description
          }}</span>
          <span class="mt-3 block text-xs font-semibold text-n-brand"
            >Criar {{ mode.label }}</span
          >
        </span>
        <span
          class="i-hugeicons-arrow-right-02 mt-1 size-4 shrink-0 text-n-slate-11"
        />
      </button>
    </div>

    <section
      class="grid min-h-0 grid-cols-1 gap-5 xl:grid-cols-[300px_minmax(0,1fr)]"
    >
      <aside class="min-w-0 space-y-5">
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="space-y-4">
            <div>
              <h2 class="m-0 text-base font-semibold">Novo lote</h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                Comece colando CPFs ou use etiquetas/arquivo.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-semibold text-white shadow-sm hover:bg-n-brand/90"
              @click="openCreateModal"
            >
              <i class="i-lucide-plus size-4" /> Novo lote
            </button>
          </div>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h2 class="m-0 text-base font-semibold">Lotes recentes</h2>
          <div class="mt-3 flex flex-wrap items-center justify-center gap-2">
            <Button
              v-if="!showDateRange"
              faded
              icon="i-lucide-list-filter"
              label="Filtrar por período"
              class="w-full justify-center"
              title="Filtrar lotes pelo período de criação"
              @click="showDateRange = true"
            />
            <DatePicker
              v-else
              :date-range="dateRange"
              :range-type="rangeType"
              teleport-to-body
              @date-range-changed="changeDateRange"
            />
            <Button
              v-if="showDateRange"
              ghost
              label="Limpar intervalo"
              @click="clearDateRange"
            />
            <small
              v-if="!filters.created_from"
              class="w-full text-center text-n-slate-11"
              >Todos os períodos. O filtro só é aplicado após confirmar o
              intervalo.</small
            >
          </div>
          <div v-if="isLoading" class="mt-3 text-sm text-n-slate-11">
            Carregando...
          </div>
          <div
            v-else
            class="mt-3 max-h-[560px] space-y-2 overflow-auto pr-1"
            @scroll="onBatchesScroll"
          >
            <article
              v-for="batch in batches"
              :key="batch.id"
              class="w-full cursor-pointer rounded-xl border border-n-weak bg-n-solid-2 p-4 text-left text-n-slate-12 shadow-sm transition hover:border-n-slate-7 hover:bg-n-alpha-1"
              :class="recentBatchCardClass(batch)"
              role="button"
              tabindex="0"
              @click="selectBatch(batch)"
              @keydown.enter.self.prevent="selectBatch(batch)"
              @keydown.space.self.prevent="selectBatch(batch)"
            >
              <div class="flex items-start justify-between gap-3">
                <span class="min-w-0 truncate text-sm font-semibold">{{
                  batch.title || `Lote #${batch.id}`
                }}</span>
                <div class="relative flex shrink-0 items-center gap-2">
                  <span
                    class="inline-flex rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] font-semibold leading-none"
                    :class="resultClass(batchVisualStatus(batch))"
                    >{{ statusLabel(batchVisualStatus(batch)) }}</span
                  >
                  <button
                    type="button"
                    class="inline-flex size-9 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2"
                    title="Ações do lote"
                    :aria-expanded="activeBatchActionsId === batch.id"
                    aria-haspopup="menu"
                    @click.stop="toggleBatchActions(batch.id, $event)"
                  >
                    <i class="i-lucide-ellipsis-vertical size-4" />
                  </button>
                  <Teleport to="body">
                    <div
                      v-if="activeBatchActionsId === batch.id"
                      :ref="element => (batchActionsMenu = element)"
                      role="menu"
                      aria-label="Ações do lote"
                      :class="batchActionsPosition.class"
                      :style="batchActionsPosition.style"
                      class="min-w-44 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 py-1 text-n-slate-12 shadow-xl"
                      @keydown.esc.stop="closeBatchActionsWithFocus"
                      @click.stop
                    >
                      <button
                        type="button"
                        role="menuitem"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-45"
                        :disabled="!canPauseBatch(batch)"
                        @click="updateBatchStatus(batch, 'pause')"
                      >
                        <i class="i-lucide-pause size-4" /> Pausar
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-45"
                        :disabled="!canResumeBatch(batch)"
                        @click="updateBatchStatus(batch, 'resume')"
                      >
                        <i class="i-lucide-play size-4" /> Retomar
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-ruby-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-45"
                        :disabled="!canCancelBatch(batch)"
                        @click="updateBatchStatus(batch, 'cancel')"
                      >
                        <i class="i-lucide-ban size-4" /> Cancelar
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2"
                        @click="openCsvDownload(batch)"
                      >
                        <i class="i-lucide-download size-4" /> Baixar CSV
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-ruby-11 hover:bg-n-alpha-2"
                        @click="deleteBatch(batch)"
                      >
                        <i class="i-lucide-trash-2 size-4" /> Excluir
                      </button>
                    </div>
                  </Teleport>
                </div>
              </div>
              <div class="mt-4 grid gap-1 text-xs text-n-slate-11">
                <div class="font-semibold">{{ batchModeLabel(batch) }}</div>
                <div>
                  <span class="font-medium text-current"
                    >Total/Processado:</span
                  >
                  {{ batch.total }}/{{ batch.processed }}
                </div>
                <div>
                  <span class="font-medium text-current">Data criada:</span>
                  {{ formatDate(batch.created_at) }}
                </div>
                <div>
                  <span class="font-medium text-current">Data finalizado:</span>
                  {{ formatDate(batch.finished_at) }}
                </div>
              </div>
            </article>
            <div
              v-if="isLoadingMore"
              class="py-2 text-center text-xs text-n-slate-11"
            >
              Carregando mais...
            </div>
          </div>
        </section>
      </aside>

      <section class="min-w-0 rounded-xl border border-n-weak bg-n-solid-1 p-5">
        <div v-if="!selectedBatch" class="text-sm text-n-slate-11">
          Selecione ou crie um lote para acompanhar os resultados.
        </div>
        <template v-else>
          <div
            class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
          >
            <div>
              <div class="mb-2 flex flex-wrap items-center gap-2">
                <span
                  class="inline-flex rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-semibold text-n-slate-11"
                  >{{ batchModeLabel(selectedBatch) }}</span
                >
                <button
                  v-if="selectedBatch.support_token"
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-md border border-n-weak px-2 py-1 font-mono text-xs text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :title="`Copiar o token do lote ${selectedBatch.support_token}`"
                  :aria-label="`Token do lote ${selectedBatch.support_token}. Copiar`"
                  @click="copySupportToken(selectedBatch.support_token)"
                >
                  <span class="i-hugeicons-copy-01 size-3.5" />
                  {{ selectedBatch.support_token }}
                </button>
              </div>
              <h2 class="m-0 text-xl font-semibold">
                {{ selectedBatch.title || `Lote #${selectedBatch.id}` }}
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                {{ selectedBatch.product_type }} · criado em
                {{ formatDate(selectedBatch.created_at) }}
              </p>
              <p class="mb-0 mt-1 text-xs text-n-slate-10">
                {{
                  (selectedBatch.providers || []).map(providerLabel).join(' · ')
                }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                v-if="hasRetryableResults"
                label="Reconsultar pendências"
                variant="faded"
                @click="updateBatchStatus(selectedBatch, 'resume')"
              />
              <button
                v-if="canPauseBatch(selectedBatch)"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium"
                @click="updateBatchStatus(selectedBatch, 'pause')"
              >
                <i class="i-lucide-pause size-4" /> Pausar
              </button>
              <button
                v-if="canResumeBatch(selectedBatch)"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium"
                @click="updateBatchStatus(selectedBatch, 'resume')"
              >
                <i class="i-lucide-play size-4" /> Retomar
              </button>
              <button
                v-if="canCancelBatch(selectedBatch)"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-ruby-7 px-4 py-2 text-sm font-medium text-n-ruby-11"
                @click="updateBatchStatus(selectedBatch, 'cancel')"
              >
                <i class="i-lucide-ban size-4" /> Cancelar
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium"
                @click="openCsvDownload(selectedBatch)"
              >
                <i class="i-lucide-download size-4" /> Baixar CSV
              </button>
            </div>
          </div>

          <div class="mt-5 grid grid-cols-2 gap-3 text-center md:grid-cols-5">
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold">
                {{ selectedBatch.total }}
              </div>
              <div class="text-xs text-n-slate-11">Consultas aos bancos</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold">
                {{ selectedBatch.processed }}
              </div>
              <div class="text-xs text-n-slate-10">Processados</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold text-n-teal-11">
                {{ selectedBatch.success_count }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ isOnlineBatch ? 'Bancos com oferta' : 'Sucesso' }}
              </div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold text-amber-600">
                {{ selectedBatch.authorization_required_count }}
              </div>
              <div class="text-xs text-n-slate-10">Autorização</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold text-red-500">
                {{ selectedBatch.error_count + selectedBatch.timeout_count }}
              </div>
              <div class="text-xs text-n-slate-10">Erros</div>
            </div>
          </div>

          <div
            class="mt-5 flex flex-wrap justify-between gap-2 text-sm text-n-slate-11"
            role="status"
          >
            <strong
              >{{ progress.processed }} de {{ progress.total }} consultas
              processadas · {{ progress.percent }}%</strong
            >
            <span>{{ progress.estimate }}</span>
          </div>
          <div
            class="mt-2 h-2 overflow-hidden rounded-full bg-n-alpha-2"
            role="progressbar"
            aria-label="Progresso das consultas"
            :aria-valuenow="progress.processed"
            :aria-valuemax="progress.total"
            aria-valuemin="0"
          >
            <div
              class="h-full bg-n-brand transition-all"
              :style="{ width: `${progress.percent}%` }"
            />
          </div>

          <div class="mt-5 flex flex-wrap items-center gap-2">
            <Button
              label="Todos"
              size="sm"
              :variant="resultFilter === 'all' ? 'solid' : 'faded'"
              @click="changeResultFilter('all')"
            />
            <Button
              v-if="isOnlineBatch"
              label="Com oferta"
              size="sm"
              :variant="resultFilter === 'offers' ? 'solid' : 'faded'"
              @click="changeResultFilter('offers')"
            />
            <Button
              label="Sucesso"
              size="sm"
              :variant="resultFilter === 'success' ? 'solid' : 'faded'"
              @click="changeResultFilter('success')"
            />
            <Button
              label="Pendências"
              size="sm"
              :variant="resultFilter === 'pending' ? 'solid' : 'faded'"
              @click="changeResultFilter('pending')"
            />
          </div>
          <p
            v-if="isOnlineBatch"
            class="mb-0 mt-3 text-xs leading-5 text-n-slate-11"
          >
            Uma oferta por CPF: a maior entre os bancos que já responderam. O
            resultado pode mudar até o fim do lote. Autorizações e dados
            pendentes devem ser resolvidos na Consulta CLT antes de reconsultar
            as pendências.
          </p>
          <div
            class="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-n-slate-11"
          >
            <span role="status" aria-live="polite">
              {{
                isLoadingResults
                  ? 'Carregando resultados...'
                  : `${resultsRange.start}–${resultsRange.end} de ${resultsRange.total} resultados`
              }}
            </span>
            <div class="flex shrink-0 items-center gap-2 whitespace-nowrap">
              <span>Por página</span>
              <Select
                :model-value="resultsPerPage"
                :disabled="isLoadingResults"
                :options="[
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                ]"
                aria-label="Resultados por página"
                @update:model-value="changeResultsPerPage"
              />
            </div>
          </div>
          <div
            class="mt-3 overflow-x-auto rounded-xl border border-n-weak"
            :aria-busy="isLoadingResults"
            :class="{ 'opacity-60': isLoadingResults }"
          >
            <table class="w-full min-w-[850px] text-left text-sm">
              <thead class="bg-n-solid-2 text-xs uppercase text-n-slate-10">
                <tr>
                  <th class="px-3 py-2">
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-lg text-left font-semibold hover:text-n-brand"
                      :title="
                        showAllCpfs
                          ? 'Ocultar CPFs completos'
                          : 'Ver CPFs completos'
                      "
                      @click="toggleAllCpfs"
                    >
                      CPF
                      <i
                        class="size-4"
                        :class="
                          showAllCpfs ? 'i-lucide-eye-off' : 'i-lucide-eye'
                        "
                      />
                    </button>
                  </th>
                  <th class="px-3 py-2">Produto</th>
                  <th class="px-3 py-2">Banco</th>
                  <th class="px-3 py-2">
                    {{ isOnlineBatch ? 'Melhor oferta' : 'Margem' }}
                  </th>
                  <th class="px-3 py-2">Status</th>
                  <th class="px-3 py-2">Resumo</th>
                  <th class="px-3 py-2">Fim</th>
                  <th class="px-3 py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="result in currentResults"
                  :key="result.id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 font-medium">
                    {{ displayCpf(result) }}
                  </td>
                  <td class="px-3 py-2">{{ result.product_type }}</td>
                  <td class="px-3 py-2 font-medium">
                    <span class="flex items-center gap-2"
                      ><BankLogo :provider="result.provider" />{{
                        result.provider_label || providerLabel(result.provider)
                      }}</span
                    >
                  </td>
                  <td class="px-3 py-2">
                    <template v-if="isOnlineBatch && result.best_offer">
                      <strong class="block whitespace-nowrap text-n-teal-11">{{
                        money(result.best_offer.released_amount)
                      }}</strong>
                      <span
                        class="mt-1 block whitespace-nowrap text-xs text-n-slate-11"
                        >{{ result.best_offer.term || '—' }} ×
                        {{ money(result.best_offer.installment) }}</span
                      >
                      <span
                        v-if="result.best_offer.rate != null"
                        class="block text-xs text-n-slate-11"
                        >Taxa: {{ result.best_offer.rate }}%</span
                      >
                      <span
                        v-if="result.best_offer.table"
                        class="block text-xs text-n-slate-11"
                        >{{ result.best_offer.table }}</span
                      >
                    </template>
                    <span v-else class="text-n-slate-11">{{
                      isOnlineBatch && !result.margin
                        ? '—'
                        : `${isOnlineBatch ? 'Margem ' : ''}${money(result.margin)}`
                    }}</span>
                  </td>
                  <td
                    class="px-3 py-2 font-semibold"
                    :class="resultClass(result.status)"
                  >
                    {{
                      result.best_offer
                        ? 'Oferta confirmada'
                        : statusLabel(result.status)
                    }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ result.response_summary || result.error_message || '-' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-2 text-n-slate-10">
                    {{ formatDateTimeSeconds(result.finished_at) }}
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex justify-end gap-1">
                      <button
                        v-if="canRetryResult(result)"
                        type="button"
                        class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Reprocessar este CPF"
                        aria-label="Reprocessar este CPF"
                        :disabled="retryingResultId === result.id"
                        @click="openRetryResult(result)"
                      >
                        <i class="i-hugeicons-reload size-4" />
                      </button>
                      <button
                        type="button"
                        class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2"
                        title="Ver log do retorno"
                        @click="openLog(result)"
                      >
                        <i class="i-lucide-file-search size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <nav
            class="mt-3 flex items-center justify-end gap-3"
            aria-label="Paginação dos resultados"
          >
            <Button
              label="Anterior"
              icon="i-lucide-chevron-left"
              variant="faded"
              size="sm"
              :disabled="isLoadingResults || resultsPagination.page <= 1"
              @click="changeResultsPage(resultsPagination.page - 1)"
            />
            <span class="text-sm text-n-slate-11"
              >Página {{ resultsPagination.page }} de
              {{ resultsPagination.total_pages }}</span
            >
            <Button
              label="Próxima"
              icon="i-lucide-chevron-right"
              variant="faded"
              size="sm"
              :disabled="isLoadingResults || !resultsPagination.has_more"
              @click="changeResultsPage(resultsPagination.page + 1)"
            />
          </nav>
          <p
            v-if="!currentResults.length"
            class="mt-4 text-center text-sm text-n-slate-11"
          >
            Nenhum resultado neste filtro.
          </p>
        </template>
      </section>
    </section>

    <div
      v-if="isCreateModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      @click.self="closeCreateModal"
    >
      <form
        class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
        aria-labelledby="new-batch-title"
        @submit.prevent="createBatch"
      >
        <header
          class="flex items-start justify-between gap-3 border-b border-n-weak px-5 py-4 md:px-6"
        >
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <i class="i-hugeicons-layers-01 size-5" />
            </span>
            <div class="min-w-0">
              <h2 id="new-batch-title" class="m-0 text-lg font-semibold">
                Novo lote
              </h2>
              <p class="mb-0 mt-0.5 text-sm text-n-slate-11">
                Consulte vários CPFs de uma vez nos bancos integrados.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2"
            aria-label="Fechar"
            @click="closeCreateModal"
          >
            <i class="i-hugeicons-cancel-01 size-5" />
          </button>
        </header>

        <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 md:px-6">
          <section class="space-y-2" aria-label="Tipo de lote">
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">
              Tipo de lote
            </h3>
            <div class="grid gap-2 sm:grid-cols-2" role="radiogroup">
              <button
                v-for="mode in batchModes"
                :key="mode.value"
                type="button"
                role="radio"
                :aria-checked="form.query_mode === mode.value"
                class="flex items-start gap-3 rounded-xl border p-3 text-left transition"
                :class="
                  form.query_mode === mode.value
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak bg-n-solid-2 hover:border-n-brand/50'
                "
                @click="form.query_mode = mode.value"
              >
                <span
                  class="flex size-9 shrink-0 items-center justify-center rounded-lg"
                  :class="
                    form.query_mode === mode.value
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  <span :class="mode.icon" class="size-5" />
                </span>
                <span class="min-w-0 flex-1">
                  <strong class="block text-sm text-n-slate-12">{{
                    mode.label
                  }}</strong>
                  <span class="mt-1 block text-xs leading-5 text-n-slate-11">{{
                    mode.description
                  }}</span>
                </span>
                <i
                  class="mt-0.5 size-5 shrink-0"
                  :class="
                    form.query_mode === mode.value
                      ? 'i-hugeicons-checkmark-circle-02 text-n-brand'
                      : 'i-hugeicons-circle text-n-slate-8'
                  "
                />
              </button>
            </div>
            <p
              v-if="form.query_mode === 'online'"
              class="mb-0 flex gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-xs leading-5 text-n-slate-11"
            >
              <i
                class="i-hugeicons-information-circle mt-0.5 size-4 shrink-0"
              />
              <span>
                Até 1.000 CPFs. O Online exige contato cadastrado e autorização
                válida do cliente. Autorizações próprias dos bancos e dados
                obrigatórios continuam sendo exigidos. Apenas simula ofertas;
                não contrata em nome do cliente.
              </span>
            </p>
          </section>

          <Input
            v-model="form.title"
            label="Nome do lote"
            placeholder="Ex: Consulta CLT setembro"
          />

          <section class="space-y-2">
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">Início</h3>
            <div
              class="grid grid-cols-2 gap-1 rounded-xl border border-n-weak bg-n-alpha-1 p-1"
              role="radiogroup"
              aria-label="Quando iniciar o lote"
            >
              <button
                v-for="option in startModeOptions"
                :key="option.value"
                type="button"
                role="radio"
                :aria-checked="form.start_mode === option.value"
                class="flex h-9 items-center justify-center gap-2 rounded-lg px-2 text-sm font-medium transition"
                :class="
                  form.start_mode === option.value
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click="form.start_mode = option.value"
              >
                <span :class="option.icon" class="size-4" />
                {{ option.label }}
              </button>
            </div>
            <DateField
              v-if="isScheduledStart"
              v-model="form.scheduled_at"
              label="Data e hora (São Paulo)"
              with-time
              :clearable="false"
              :min="currentDateTime"
              message="O lote começa automaticamente no horário escolhido."
            />
            <p v-else class="mb-0 text-xs text-n-slate-10">
              O lote começa assim que for criado.
            </p>
          </section>

          <section class="space-y-2">
            <div class="flex flex-wrap items-baseline justify-between gap-2">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">Bancos</h3>
              <span class="text-xs text-n-slate-10">
                {{ form.provider_codes.length }} de
                {{ availableBankCount }} disponível(is) em lote
              </span>
            </div>
            <TagMultiSelectComboBox
              v-model="form.provider_codes"
              :options="bankOptions"
              placeholder="Selecione os bancos"
              search-placeholder="Buscar banco"
              empty-state="Nenhum banco ativo encontrado."
            >
              <template #tag="{ tag }">
                <span class="flex min-w-0 items-center gap-1.5">
                  <BankLogo
                    :provider="tag.value"
                    class="!size-5 !rounded-md [&>img]:!h-5 [&>img]:!p-0.5"
                  />
                  <span class="truncate text-sm text-n-slate-12">{{
                    tag.label
                  }}</span>
                </span>
              </template>
              <template #option="{ option, selected }">
                <span class="flex min-w-0 items-center gap-2.5">
                  <BankLogo :provider="option.value" />
                  <span class="min-w-0">
                    <span
                      class="block truncate text-n-slate-12"
                      :class="{ 'font-medium': selected }"
                      >{{ option.label }}</span
                    >
                    <span
                      class="block text-xs leading-4"
                      :class="
                        option.disabled
                          ? 'text-n-slate-10'
                          : option.reason
                            ? 'text-n-amber-11'
                            : 'text-n-teal-11'
                      "
                      >{{
                        option.reason || 'Disponível para consulta em lote'
                      }}</span
                    >
                  </span>
                </span>
              </template>
            </TagMultiSelectComboBox>
            <p class="mb-0 text-xs text-n-slate-10">
              {{
                form.query_mode === 'online'
                  ? 'O Online consulta margem e simula em todos os bancos ativos. Quando um banco exige assinatura, link ou código do cliente, o CPF fica pendente para seguir na Consulta CLT.'
                  : 'O Offline consulta a base da Facta e da Presença Bank. Os demais bancos ficam disponíveis no Lote Online.'
              }}
            </p>
            <label
              v-if="form.query_mode === 'online'"
              class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3"
              :class="{
                'cursor-not-allowed opacity-60': !confirmeOnline.configured,
              }"
            >
              <Checkbox
                v-model="form.enrich_customer_data"
                class="mt-0.5"
                :disabled="!confirmeOnline.configured"
              />
              <span class="min-w-0 text-sm">
                <strong class="block text-n-slate-12">
                  Completar dados faltantes com o Talkvia Cadastro
                </strong>
                <span class="mt-0.5 block text-xs leading-5 text-n-slate-11">
                  {{
                    confirmeOnline.configured
                      ? `Busca nascimento e nome da mãe só quando faltarem, uma vez por CPF. Usa a franquia mensal e depois os créditos comprados. Saldo: ${Number(confirmeOnline.balance || 0).toLocaleString('pt-BR')}.`
                      : 'Talkvia Cadastro não configurado nesta instalação.'
                  }}
                </span>
              </span>
            </label>
          </section>

          <section class="flex flex-col gap-2">
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">Público</h3>
            <div
              class="grid grid-cols-3 gap-1 rounded-xl border border-n-weak bg-n-alpha-1 p-1"
              role="tablist"
            >
              <button
                v-for="option in audienceModeOptions"
                :key="option.value"
                type="button"
                role="tab"
                :aria-selected="form.audience_mode === option.value"
                class="flex h-9 items-center justify-center gap-1 rounded-lg px-1 text-xs font-medium transition sm:gap-2 sm:px-2 sm:text-sm"
                :class="
                  form.audience_mode === option.value
                    ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12'
                "
                @click="form.audience_mode = option.value"
              >
                <span :class="option.icon" class="size-4" />
                <span class="truncate">{{ option.label }}</span>
              </button>
            </div>

            <div v-if="form.audience_mode === 'labels'" class="space-y-3">
              <Input
                v-model="labelSearch"
                placeholder="Buscar etiquetas cadastradas"
              />
              <div
                v-if="selectedLabelTitles.length"
                class="flex flex-wrap gap-2"
              >
                <button
                  v-for="title in selectedLabelTitles"
                  :key="`selected-${title}`"
                  type="button"
                  class="inline-flex items-center gap-2 rounded-full border border-n-brand bg-n-brand/10 px-3 py-1 text-xs font-medium text-n-brand"
                  @click="toggleLabel(title)"
                >
                  {{ title }}
                  <i class="i-lucide-x size-3" />
                </button>
              </div>
              <div
                class="max-h-44 overflow-auto rounded-lg border border-n-weak bg-n-solid-2 p-2"
              >
                <button
                  v-for="label in filteredLabels"
                  :key="label.id || label.title"
                  type="button"
                  class="mb-2 mr-2 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                  :class="
                    hasSelectedLabel(label.title)
                      ? 'border-n-brand bg-n-brand/10 text-n-brand'
                      : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand'
                  "
                  @click="toggleLabel(label.title)"
                >
                  <span
                    class="size-2 rounded-full"
                    :style="{ backgroundColor: label.color || '#64748b' }"
                  />
                  {{ label.title }}
                </button>
                <div
                  v-if="!filteredLabels.length"
                  class="p-2 text-sm text-n-slate-11"
                >
                  Nenhuma etiqueta encontrada.
                </div>
              </div>
              <p class="mb-0 text-xs text-n-slate-11">
                Somente contatos das etiquetas selecionadas com CPF válido
                cadastrado entram no lote. Cada CPF será consultado uma única
                vez por banco.
              </p>
            </div>

            <TextArea
              v-else-if="form.audience_mode === 'paste'"
              v-model="form.cpfs"
              :max-length="100000"
              min-height="9rem"
              resize
              placeholder="Cole CPFs. Separe por linha, vírgula ou ponto e vírgula."
              @blur="normalizeCpfs"
            />

            <label
              v-else
              class="flex min-h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-4 py-5 text-center hover:bg-n-alpha-2"
            >
              <span class="i-lucide-file-up size-5 text-n-slate-11" />
              <span class="text-sm font-medium text-n-slate-12">
                Enviar CSV ou TXT
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  form.file?.name ||
                  'O arquivo pode ter CPFs em qualquer coluna.'
                }}
              </span>
              <input
                ref="fileInput"
                class="hidden"
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                @change="onFileChange"
              />
            </label>
            <p
              v-if="isLoadingAudience"
              class="mb-0 text-xs text-n-slate-11"
              role="status"
            >
              Verificando contatos com CPF válido…
            </p>
            <p
              v-else-if="audienceError"
              class="mb-0 text-xs text-n-ruby-11"
              role="alert"
            >
              Não foi possível verificar as etiquetas. Selecione-as novamente
              para tentar outra vez.
            </p>
            <p
              v-else-if="form.audience_mode === 'labels'"
              class="mb-0 text-xs text-n-slate-11"
              role="status"
            >
              {{ audienceCount }} CPF(s) válido(s) encontrado(s), sem
              duplicação.
            </p>
            <p v-else class="mb-0 text-xs text-n-slate-11">
              {{ audienceCount }}
              {{
                form.audience_mode === 'file'
                  ? 'arquivo(s) selecionado(s)'
                  : 'destinatário(s) encontrado(s)'
              }}
            </p>
          </section>
        </div>

        <footer
          class="flex flex-col-reverse gap-2 border-t border-n-weak px-5 py-4 sm:flex-row sm:items-center sm:justify-end md:px-6"
        >
          <Button
            type="button"
            variant="faded"
            color="slate"
            label="Cancelar"
            @click="closeCreateModal"
          />
          <Button
            type="submit"
            :label="submitLabel"
            :icon="
              isScheduledStart ? 'i-hugeicons-calendar-03' : 'i-hugeicons-play'
            "
            :is-loading="isCreating"
            :disabled="isCreating || !canCreateBatch || !labelAudienceReady"
          />
        </footer>
      </form>
    </div>

    <div
      v-if="logResult"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      @click.self="closeLog"
    >
      <section
        class="max-h-[85vh] w-full max-w-4xl overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-2xl"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 class="m-0 text-lg font-semibold">Log do retorno</h2>
            <p class="mb-0 mt-1 text-sm text-n-slate-11">
              {{ logResult.product_type }} ·
              {{ logResult.cpf_full || logResult.cpf }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeLog"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </div>
        <pre
          class="max-h-[60vh] overflow-auto rounded-lg bg-n-solid-2 p-4 text-xs leading-5 text-n-slate-11"
          >{{ JSON.stringify(logResult.raw_response || {}, null, 2) }}</pre
        >
      </section>
    </div>
  </main>
  <Dialog
    ref="exportDialog"
    title="Baixar resultados do lote"
    description="O CSV inclui o CPF completo dos clientes. Escolha quais resultados deseja baixar."
    confirm-button-label="Baixar CSV"
    :is-loading="isDownloading"
    :disable-confirm-button="isDownloading"
    @confirm="downloadCsv"
  >
    <div class="flex flex-col gap-3">
      <label
        class="flex cursor-pointer items-center gap-3 rounded-xl border border-n-weak p-4"
      >
        <input
          v-model="exportStatus"
          type="radio"
          value="all"
          name="export-status"
        />
        <span>{{ 'Todos os resultados' }}</span>
      </label>
      <label
        class="flex cursor-pointer items-center gap-3 rounded-xl border border-n-weak p-4"
      >
        <input
          v-model="exportStatus"
          type="radio"
          value="success"
          name="export-status"
        />
        <span>{{ 'Apenas com sucesso' }}</span>
      </label>
    </div>
  </Dialog>
  <Dialog
    ref="retryDialog"
    title="Reprocessar este CPF?"
    description="A consulta será refeita no banco somente para este CPF, sem retomar os demais. Se o lote estiver pausado, ele continua pausado."
    confirm-button-label="Reprocessar"
    :is-loading="Boolean(retryingResultId)"
    :disable-confirm-button="Boolean(retryingResultId)"
    @confirm="retryResult"
  >
    <p v-if="retryTarget" class="mb-0 text-sm text-n-slate-11">
      {{ retryTarget.cpf_full || retryTarget.cpf }} ·
      {{ retryTarget.provider_label || providerLabel(retryTarget.provider) }}
    </p>
  </Dialog>
</template>

<style scoped>
.facta-date-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  opacity: 0.7;
}

.facta-date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}
</style>
